import React, { useState } from 'react';

const TournamentFilters = ({ filters, onFilterChange, onReset }) => {
  const [activeSection, setActiveSection] = useState(null);

  // Filter option definitions
  const filterOptions = {
    game: [
      { value: "all", label: "All Games" },
      { value: "freefire", label: "Free Fire", icon: "🔥" },
      { value: "pubg", label: "PUBG", icon: "🔫" },
      { value: "valorant", label: "Valorant", icon: "🎯" },
      { value: "cod", label: "Call of Duty", icon: "🎖️" }
    ],
    tournamentType: [
      { value: "all", label: "All Types" },
      { value: "solo", label: "Solo", icon: "👤" },
      { value: "duo", label: "Duo", icon: "👥" },
      { value: "squad", label: "Squad", icon: "👨‍👩‍👧‍👦" }
    ],
    entryFeeType: [
      { value: "all", label: "Any Fee" },
      { value: "free", label: "Free Entry", icon: "🆓" },
      { value: "coins", label: "Coin Entry", icon: "🪙" },
      { value: "paid", label: "Paid Entry", icon: "💰" }
    ],
    status: [
      { value: "all", label: "Any Status" },
      { value: "upcoming", label: "Upcoming", icon: "📅" },
      { value: "ongoing", label: "Ongoing", icon: "🔄" },
      { value: "completed", label: "Completed", icon: "✅" },
      { value: "registration_open", label: "Registration Open", icon: "📝" }
    ],
    registered: [
      { value: "all", label: "All" },
      { value: "registered", label: "Registered", icon: "✓" },
      { value: "not_registered", label: "Not Registered", icon: "+" }
    ]
  };

  // Filter section labels and icons
  const filterSections = [
    { key: "game", label: "Games", icon: "🎮" },
    { key: "tournamentType", label: "Type", icon: "👥" },
    { key: "entryFeeType", label: "Fee", icon: "💰" },
    { key: "status", label: "Status", icon: "🔄" },
    { key: "registered", label: "Registration", icon: "📝" }
  ];

  // Handle filter option click
  const handleFilterClick = (filterType, value) => {
    console.log(`Filter changed: ${filterType} = ${value}`);
    
    // If selecting "completed" status, show loading indicator or message
    if (filterType === "status" && value === "completed") {
      console.log("Fetching completed tournaments...");
    }
    
    onFilterChange(filterType, value);
    setActiveSection(null); // Close dropdown after selection
  };

  // Toggle filter section visibility
  const toggleFilterSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="flex items-center text-sm text-gray-500 mr-1">
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters:
        </div>

        {/* Filter Selector Buttons */}
        {filterSections.map((section) => (
          <div key={section.key} className="relative">
            <button
              onClick={() => toggleFilterSection(section.key)}
              className={`flex items-center text-sm px-3 py-1.5 border rounded-md transition-colors ${
                filters[section.key] !== "all"
                  ? "bg-primary-50 border-primary-200 text-primary-700"
                  : "bg-gray-50 border-gray-200 hover:bg-gray-100"
              }`}
            >
              <span className="mr-1">{section.icon}</span>
              <span>{section.label}</span>
              {filters[section.key] !== "all" && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary-100 text-primary-800 text-xs rounded-full">
                  {filterOptions[section.key].find(opt => opt.value === filters[section.key])?.label || filters[section.key]}
                </span>
              )}
              <svg
                className={`w-4 h-4 ml-1 transition-transform ${
                  activeSection === section.key ? "transform rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Options */}
            {activeSection === section.key && (
              <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200 py-1">
                {filterOptions[section.key].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterClick(section.key, option.value)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                      filters[section.key] === option.value
                        ? "bg-primary-50 text-primary-700 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {option.icon && <span className="mr-2">{option.icon}</span>}
                    {option.label}
                    {filters[section.key] === option.value && (
                      <svg className="w-4 h-4 ml-auto text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Show active filters summary */}
        <div className="flex-grow"></div>

        {/* Reset button */}
        {Object.values(filters).some(value => value !== "all") && (
          <button
            onClick={onReset}
            className="text-xs text-red-600 hover:text-red-800 transition-colors flex items-center"
          >
            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Reset Filters
          </button>
        )}
      </div>

      {/* Active Filters Display */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(filters).map(([key, value]) => {
          if (value === "all") return null;
          const filterSection = filterSections.find(s => s.key === key);
          const option = filterOptions[key].find(opt => opt.value === value);
          
          if (!option) return null;
          
          return (
            <div 
              key={key} 
              className="inline-flex items-center text-xs bg-primary-50 text-primary-700 rounded-full px-2.5 py-1 border border-primary-100"
            >
              <span className="mr-1">{filterSection?.icon}</span>
              <span>{filterSection?.label}: {option.label}</span>
              <button 
                onClick={() => handleFilterClick(key, "all")}
                className="ml-1 text-primary-500 hover:text-primary-700"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TournamentFilters;
